import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiClient } from './client.js';
import { useApi } from './use-api.js';

/**
 * Browser-side Group types. Server-of-record is
 * `packages/api/src/registry/group-registry-types.ts`. Optional/nullable
 * fields are tolerated as `unknown` — the UI only renders the basics.
 */
export interface GroupEntry {
  readonly name: string;
  readonly members: readonly string[];
  readonly createdAt: string;
  readonly updatedAt?: string;
  readonly lastSyncedAt?: string | null;
  readonly lastContractCount?: number | null;
  readonly lastSyncStatus?: 'ok' | 'error' | 'pending' | 'never';
  readonly lastError?: string | null;
}

export interface CreateGroupBody {
  readonly name: string;
  readonly members: readonly string[];
}

export async function listGroups(api: ApiClient): Promise<readonly GroupEntry[]> {
  const res = await api.request<{ groups: GroupEntry[] }>('/api/groups');
  return res.groups;
}

export async function createGroup(api: ApiClient, body: CreateGroupBody): Promise<GroupEntry> {
  const res = await api.request<{ group: GroupEntry }>('/api/groups', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return res.group;
}

export function useGroups() {
  const api = useApi();
  return useQuery({ queryKey: ['groups'], queryFn: () => listGroups(api) });
}

export function useCreateGroup(onSuccess?: (group: GroupEntry) => void) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateGroupBody) => createGroup(api, body),
    onSuccess: (group) => {
      void qc.invalidateQueries({ queryKey: ['groups'] });
      onSuccess?.(group);
    },
  });
}

export async function syncGroup(api: ApiClient, name: string): Promise<string> {
  const res = await api.request<{ jobId: string }>(`/api/groups/${encodeURIComponent(name)}/sync`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
  return res.jobId;
}

export async function deleteGroup(api: ApiClient, name: string): Promise<void> {
  await api.request<void>(`/api/groups/${encodeURIComponent(name)}`, { method: 'DELETE' });
}

export function useSyncGroup() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => syncGroup(api, name),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['groups'] });
    },
  });
}

export function useDeleteGroup(onSuccess?: () => void) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => deleteGroup(api, name),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['groups'] });
      onSuccess?.();
    },
  });
}
